// SGNL-206 — Pipeline Analytics tab.
//
// Wraps the existing Stage Velocity + Conversion Funnel views as
// sub-tabs of a single Pipeline Analytics top-nav entry. Both are
// stage-axis analytics with very similar layouts; collapsing them
// reduces top-nav clutter and the context-switch when flipping
// between them.
//
// Sub-tab state lives in the URL hash (#pipeline_analytics?sub=funnel)
// so each sub-view is bookmarkable. The "velocity" sub is the default;
// the canonical URL for velocity is bare #pipeline_analytics (no
// ?sub= param) so a fresh share-link stays clean. Both #pipeline_analytics
// and #pipeline_analytics?sub=velocity resolve to the same sub —
// adding a future third sub with a different default would require a
// migration of the bare-URL meaning.
//
// The standalone #stage_velocity / #conversion_funnel routes remain
// live until SGNL-207 redirects them through this tab.

import {
  _abortConversionFunnel,
  _abortStageVelocity,
  renderConversionFunnelTab,
  renderStageVelocityTab,
} from "./analytics";
import { abortRevenueProjection, renderRevenueProjectionTab } from "./revenue_projection";
import { parseRoute } from "../router";

// SGNL-213 — added "projection" as the third sub-view. The shared
// shell handles sub-tab routing + ARIA tablist + abort-on-swap; the
// new sub plugs into mountSub below.
export type PipelineAnalyticsSub = "velocity" | "funnel" | "projection";

const DEFAULT_SUB: PipelineAnalyticsSub = "velocity";

const VALID_SUBS: ReadonlySet<PipelineAnalyticsSub> = new Set([
  "velocity",
  "funnel",
  "projection",
]);

/** Read the active sub from the URL hash. Returns the default if the
 * hash doesn't carry a sub param, or if the param value is unknown. */
export function pipelineAnalyticsSubFromHash(hash: string): PipelineAnalyticsSub {
  const route = parseRoute(hash);
  if (route.kind !== "tab" || route.tab !== "pipeline_analytics") return DEFAULT_SUB;
  const sub = route.params.get("sub");
  if (sub && VALID_SUBS.has(sub as PipelineAnalyticsSub)) {
    return sub as PipelineAnalyticsSub;
  }
  return DEFAULT_SUB;
}

/** Build the URL hash for a given sub. PRA-2: preserves any other
 * hash params that might exist (today there are none, but defensive
 * against future params). The default sub drops the ?sub= param so
 * the canonical URL stays clean. */
function buildHashForSub(sub: PipelineAnalyticsSub, currentHash: string): string {
  const qIdx = currentHash.indexOf("?");
  const params = new URLSearchParams(qIdx >= 0 ? currentHash.slice(qIdx + 1) : "");
  if (sub === DEFAULT_SUB) {
    params.delete("sub");
  } else {
    params.set("sub", sub);
  }
  const qs = params.toString();
  return qs ? `#pipeline_analytics?${qs}` : "#pipeline_analytics";
}

/** Render the Pipeline Analytics tab. Wires the sub-tab toggle and
 * delegates the body to the existing analytics renderers.
 *
 * ARIA: implements the standard tabs pattern per the ARIA APG —
 * role="tablist" on the strip; role="tab" + aria-selected on each
 * button; role="tabpanel" + aria-labelledby on the body. Replaces
 * the prior role="group" + aria-pressed pattern (CR F-04 / UI F-08)
 * which read as toggle buttons to screen readers; this surface is
 * structurally a tab widget (content swaps when the active tab
 * changes). */
export async function renderPipelineAnalyticsTab(host: HTMLElement): Promise<void> {
  const initialSub = pipelineAnalyticsSubFromHash(window.location.hash);

  // The tab-header uses the class's existing flex layout. Sub-tab
  // strip is the second flex child alongside the h2. Removed the
  // inline style that previously overrode .tab-header's margin-bottom
  // (UI F-05) — the CSS rule now carries everything.
  host.innerHTML = `
    <div class="tab-content pipeline-analytics-tab" data-testid="pipeline-analytics-tab">
      <div class="tab-header pa-tab-header">
        <h2>Pipeline Analytics</h2>
        <div class="pa-subtabs" role="tablist" aria-label="Pipeline Analytics sub-tabs" data-testid="pa-subtabs">
          <button type="button"
                  class="pa-subtab ${initialSub === "velocity" ? "active" : ""}"
                  role="tab"
                  id="pa-tab-velocity"
                  aria-controls="pa-body"
                  aria-selected="${initialSub === "velocity"}"
                  data-action="pa-subtab"
                  data-sub="velocity"
                  data-testid="pa-subtab-velocity">
            Stage Velocity
          </button>
          <button type="button"
                  class="pa-subtab ${initialSub === "funnel" ? "active" : ""}"
                  role="tab"
                  id="pa-tab-funnel"
                  aria-controls="pa-body"
                  aria-selected="${initialSub === "funnel"}"
                  data-action="pa-subtab"
                  data-sub="funnel"
                  data-testid="pa-subtab-funnel">
            Funnel
          </button>
          <button type="button"
                  class="pa-subtab ${initialSub === "projection" ? "active" : ""}"
                  role="tab"
                  id="pa-tab-projection"
                  aria-controls="pa-body"
                  aria-selected="${initialSub === "projection"}"
                  data-action="pa-subtab"
                  data-sub="projection"
                  data-testid="pa-subtab-projection">
            Revenue Projection
          </button>
        </div>
      </div>
      <div class="pipeline-analytics-body"
           id="pa-body"
           role="tabpanel"
           aria-labelledby="pa-tab-${initialSub}"
           data-testid="pa-body"></div>
    </div>`;

  const body = host.querySelector<HTMLElement>("#pa-body")!;

  // Bind click handlers BEFORE starting the async initial mount. The
  // e2e regression that caught this: clicking a sub-tab during the
  // initial velocity load was a no-op because the button had no
  // listener attached yet (handlers were bound AFTER the await).
  // Now the strip is interactive immediately on innerHTML write.
  // Sub-tab click handlers.
  for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-action="pa-subtab"]')) {
    btn.addEventListener("click", async () => {
      const sub = btn.dataset.sub as PipelineAnalyticsSub | undefined;
      if (!sub) return;
      // URL update FIRST (same pattern as SGNL-205's chip strip — any
      // re-entrant render path sees the correct hash, not a stale one).
      const newHash = buildHashForSub(sub, window.location.hash);
      if (window.location.hash !== newHash) {
        history.replaceState(
          history.state,
          "",
          window.location.pathname + window.location.search + newHash,
        );
      }
      // Update active state on all tabs (only one is selected at a time).
      for (const b of host.querySelectorAll<HTMLButtonElement>('[data-action="pa-subtab"]')) {
        const isActive = b.dataset.sub === sub;
        b.classList.toggle("active", isActive);
        b.setAttribute("aria-selected", String(isActive));
      }
      body.setAttribute("aria-labelledby", `pa-tab-${sub}`);
      await mountSub(body, sub);
    });
  }

  // Initial mount of the active sub-view, fired after the click
  // handlers are wired so a rapid sub-click during the load is
  // captured correctly.
  await mountSub(body, initialSub);
}

/** Delegate body rendering to the existing sub-view renderer.
 *
 * CR F-01 / PRA-1: aborts BOTH renderers' in-flight fetches against
 * `body` before mounting the new one. analytics.ts only aborts its
 * own renderer's prior fetch — switching subs would otherwise leave
 * the sibling renderer's fetch running, and when it resolved it
 * would overwrite the body with stale content. The dual abort
 * before mount is the racing-mount guard. */
async function mountSub(body: HTMLElement, sub: PipelineAnalyticsSub): Promise<void> {
  _abortStageVelocity(body);
  _abortConversionFunnel(body);
  // SGNL-213 architect MED pre-PR review: the Revenue Projection
  // view registers a subscribe listener that fires renderBody on every
  // state notify. innerHTML="" destroys the DOM but DOES NOT detach
  // the subscribe listener (it lives in state.ts's listeners array by
  // reference). Without abortRevenueProjection here, swapping
  // Projection → Velocity would leave a stale listener writing to a
  // detached body element. mountSub must drive teardown so all 3
  // sub-tab swap directions are clean.
  abortRevenueProjection(body);
  // Clear the body immediately so the user sees an empty card while
  // the new sub loads, rather than the stale prior sub's content.
  body.innerHTML = "";
  if (sub === "funnel") {
    await renderConversionFunnelTab(body);
  } else if (sub === "projection") {
    // SGNL-213: Revenue Projection is a synchronous render — no fetch
    // (the projection is a pure function of state.data + the preset).
    // The renderer hooks its own subscribe listener; teardown lives
    // in abortRevenueProjection above so a swap away from this sub
    // unhooks cleanly.
    renderRevenueProjectionTab(body);
  } else {
    // Exhaustive fallback — any future PipelineAnalyticsSub value
    // not explicitly handled defaults to Stage Velocity (the original
    // default). Architect INFO pre-PR review: explicit comment so the
    // fallback intent is unmistakable.
    await renderStageVelocityTab(body);
  }
}
